import {
  BuilderCommonArgs,
  CheckerContext,
  CornerChecker,
  CutoutChecker,
  EdgeChecker,
  EquivalentProductChecker,
  PlumbingChecker,
  SelectedItemChecker,
  TearOutChecker,
} from '../checkers/equivalentProductMatchingCheckers';
import {
  CornerInfoWithProductInfo,
  DrawingShapeItem,
  ProductMatchingSummary,
  SelectedItemProductMatchingSummary,
  SelectedItemWithFullRecord,
} from '../interfaces/ProductMatchingSummary';

import { SIDE_ITEM_TYPE_MATERIAL_MAP } from '~/core/constants';
import { ShapeType, SideItemType } from '~/core/enums';
import {
  ApplianceModel,
  CommonProductModel,
  CornerRadiusModel,
  CutoutModel,
  DrawingAppliance,
  DrawingShape,
  EdgeBySideItem,
  EdgeModel,
  PlumbingProduct,
  SelectedItemProperties,
  SideItemInfoModel,
  TearOutModel,
} from '~/core/models';
import { OptionalItem } from '~/core/models/optional-items-response';
import { getProductDescription, getSideItemsFromShape } from '~/core/utils';

/**
 * Type guard that checks whether a given {@link DrawingShapeItem}
 * is a {@link DrawingAppliance}.
 * @param {DrawingShapeItem} item - The drawing item to check.
 * @returns {item is DrawingAppliance} Returns `true` if the item is a `DrawingAppliance`, otherwise `false`.
 */
export const isAppliance = (item: DrawingShapeItem): item is DrawingAppliance =>
  !!(item as DrawingAppliance).applianceModel;

/**
 * To filter out undefined
 */
export const isSelectedItemWithPropertiesAndIsSelected = (
  selectedItemWithProperties?: SelectedItemWithFullRecord
): selectedItemWithProperties is SelectedItemWithFullRecord =>
  selectedItemWithProperties !== undefined &&
  selectedItemWithProperties.selectedItemProperties.isSelected;

/**
 * Higher order function that returns a function to find an item in a catalog that match by product id */
export const isEqToProductId =
  (productId: number) => (catalogItem: { productId: number }) =>
    catalogItem.productId === productId;

/**
 * Higher order function that returns a function to find an equivalent item in a catalog that match by masterSku
 * */
export const isEqToMasterSku =
  (masterSku?: string) => (catalogItem: { masterSku?: string }) =>
    !!masterSku && catalogItem.masterSku === masterSku;

/**
 * Return a function that takes a [string, SelectedItemProperties] and returns [CommonProductModel, SelectedItemProperties].
 * In order to accomplish this, a catalog is required. The arg products is the catalog
 * @param products the current product catalog
 */
export const getFullRecordProductFromCatalog =
  <T extends { productId: number }>(products: T[]) =>
  ([selectedProductId, selectedItemProperties]: [
    number | string,
    SelectedItemProperties,
  ]):
    | undefined
    | { selectedItemProperties: SelectedItemProperties; selectedItem: T } => {
    const productFullRecord = products.find(
      isEqToProductId(Number(selectedProductId))
    );

    return productFullRecord && selectedItemProperties.quantity > 0
      ? {
          selectedItem: productFullRecord,
          selectedItemProperties,
        }
      : undefined;
  };

export const addToSummary = <
  SelectedItemModel,
  CatalogProductModel extends Pick<CommonProductModel, 'productId'>,
  N,
>(args: {
  summary: ProductMatchingSummary<SelectedItemModel, N>;
  item: SelectedItemModel;
  index: number;
  checker: EquivalentProductChecker<SelectedItemModel, CatalogProductModel, N>;
  ctx: CheckerContext<CatalogProductModel>;
}): ProductMatchingSummary<SelectedItemModel, N> => {
  const { summary, item, index, checker, ctx } = args;

  const _existsInNewCatalog = checker.existsInNewCatalog({ item, ctx });
  const equivalent = checker.findEquivalent({ item, ctx });

  const builderCommonArguments: BuilderCommonArgs<
    SelectedItemModel,
    CatalogProductModel,
    N
  > = {
    ctx,
    index,
    item,
    oldValue: summary.newValueAfterReplacements,
  };

  const buildNewDesignItemOnRemoving =
    checker.buildNewDesignItemOnRemoving ??
    ((args: BuilderCommonArgs<SelectedItemModel, CatalogProductModel, N>) =>
      args.oldValue);

  if (_existsInNewCatalog) {
    // Keep
    const newValueAfterReplacements = checker.buildNewDesignItemOnKeepingItem(
      builderCommonArguments
    );

    return {
      ...summary,
      newValueAfterReplacements,
      kept: [...summary.kept, item],
    };
  } else if (!_existsInNewCatalog && equivalent) {
    // Replace with Equivalent
    const newValueAfterReplacements =
      checker.buildNewDesignItemOnReplacingWithEquivalent({
        ...builderCommonArguments,
        equivalent,
      });

    return {
      ...summary,
      newValueAfterReplacements,
      replacedWithEquivalent: [
        ...summary.replacedWithEquivalent,
        { original: item, replacement: equivalent },
      ],
    };
  } else if (checker.selectDefault && checker.buildNewValueUsingDefault) {
    // Removing or replacing with default
    const defaultItem = checker.selectDefault({ ctx });
    const newValueAfterReplacements = checker.buildNewValueUsingDefault({
      ...builderCommonArguments,
      defaultItem,
    });

    return {
      ...summary,
      newValueAfterReplacements,
      removed: [...summary.removed, item],
    };
  } else {
    const newValueAfterReplacements = buildNewDesignItemOnRemoving(
      builderCommonArguments
    );

    return {
      ...summary,
      newValueAfterReplacements,
      removed: [...summary.removed, item],
    };
  }
};

/**
 * Higher order function that return a reducer.
 * @returns a reducer. {@link DrawingShape} to {@link ProductMatchingSummary}
 */
export const createDrawingShapesToSummaryReducer =
  (edgesNewCatalog: EdgeModel[]) =>
  (
    acc: ProductMatchingSummary<EdgeModel, DrawingShape>,
    next: DrawingShape,
    index: number
  ): ProductMatchingSummary<EdgeModel, DrawingShape> => {
    const edge = next.edge;

    if (!edge) {
      return acc;
    }

    const ctx: CheckerContext<EdgeModel> = {
      newCatalog: edgesNewCatalog,
    };

    return addToSummary({
      summary: acc,
      item: edge,
      index,
      checker: EdgeChecker,
      ctx,
    });
  };

export const ApplianceChecker: EquivalentProductChecker<
  DrawingAppliance,
  ApplianceModel,
  DrawingShape
> = {
  existsInNewCatalog: ({ item, ctx }) =>
    ctx.newCatalog.some(isEqToProductId(item.applianceModel.productId)),
  findEquivalent: ({ item, ctx }) => {
    const equivalentApplianceModel = ctx.newCatalog.find(
      isEqToMasterSku(item.applianceModel.masterSku)
    );

    return equivalentApplianceModel
      ? { ...item, applianceModel: equivalentApplianceModel }
      : undefined;
  },
  buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,
  buildNewDesignItemOnReplacingWithEquivalent: ({
    item,
    oldValue: clonedDrawingShapes,
    index,
    equivalent,
  }) => {
    //Mutating drawing shape appliances in clone
    clonedDrawingShapes[index].appliances.forEach((drawingAppliance) => {
      if (
        drawingAppliance.applianceModel.productId ===
        item.applianceModel.productId
      ) {
        drawingAppliance.applianceModel = equivalent.applianceModel;
      }
    });

    const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(
      index,
      clonedDrawingShapes[index]
    );

    return newValueAfterReplacements;
  },
  buildNewDesignItemOnRemoving: ({
    item,
    oldValue: clonedDrawingShapes,
    index,
  }) => {
    //Mutating drawing shape appliances in clone
    clonedDrawingShapes[index].appliances = clonedDrawingShapes[
      index
    ].appliances.filter(
      (drawingAppliance) =>
        drawingAppliance.applianceModel.productId !==
        item.applianceModel.productId
    );
    const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(
      index,
      clonedDrawingShapes[index]
    );

    return newValueAfterReplacements;
  },
};

/**
 * Higher order function that return a reducer.
 * @returns a reducer. {@link DrawingShape} to {@link ProductMatchingSummary}
 */
export const createDrawingShapesToSummaryReducerUsingApplianceChecker =
  (newCatalog: ApplianceModel[]) =>
  (
    acc: ProductMatchingSummary<DrawingAppliance, DrawingShape>,
    next: DrawingShape,
    index: number
  ): ProductMatchingSummary<DrawingAppliance, DrawingShape> => {
    const drawingAppliances = next.appliances;

    if (!drawingAppliances.length) {
      return acc;
    }

    const ctx: CheckerContext<ApplianceModel> = {
      newCatalog,
    };

    return drawingAppliances.reduce(
      (_acc, drawingAppliance) =>
        addToSummary({
          summary: _acc,
          item: drawingAppliance,
          index,
          checker: ApplianceChecker,
          ctx,
        }),
      acc
    );
  };

export const createDrawingShapesToSummaryReducerUsingCornersChecker =
  (newCatalog: CornerRadiusModel[], currentCatalog: CornerRadiusModel[]) =>
  (
    acc: ProductMatchingSummary<CornerInfoWithProductInfo, DrawingShape>,
    next: DrawingShape,
    index: number
  ): ProductMatchingSummary<CornerInfoWithProductInfo, DrawingShape> => {
    const cornerMap = next.cornerMap;

    if (!Object.keys(cornerMap).length) {
      return acc;
    }

    const ctx: CheckerContext<CornerRadiusModel> = {
      newCatalog,
    };

    return Object.entries(cornerMap)
      .map<CornerInfoWithProductInfo | undefined>(([key, cornerInfo]) => {
        const matchingCorner = currentCatalog.find(
          (corner) => corner.productId === cornerInfo.productId
        );

        return matchingCorner
          ? {
              key,
              cornerInfo,
              masterSku: matchingCorner?.masterSku,
              productName: getProductDescription(matchingCorner),
              imageUrl: matchingCorner.imageUrl,
            }
          : undefined;
      })
      .filter(
        (
          cornerFullRecordOrUndefined
        ): cornerFullRecordOrUndefined is CornerInfoWithProductInfo =>
          !!cornerFullRecordOrUndefined
      )
      .filter((cornerFullRecord) => cornerFullRecord.cornerInfo.productId) //check if is product
      .reduce(
        (_acc, cornerFullRecord) =>
          addToSummary({
            summary: _acc,
            item: cornerFullRecord,
            index,
            checker: CornerChecker,
            ctx,
          }),
        acc
      );
  };

/**
 * Extracts the `sideType` values for items whose `materialFilter`
 * does NOT match the given `newMaterial`.
 *
 * An item is included when:
 *   - `newMaterial` exists
 *   - the item has a `materialFilter`
 *   - `newMaterial` does NOT match `materialFilter`
 *
 * @param {string} newMaterial - The material string to compare against item filters.
 * @returns {Array<SideItemType>} Array of `SideItemType` values that should be
 * removed.
 */
export const getSideItemTypesToRemove = (
  newMaterial: string | null | undefined
): SideItemType[] =>
  Object.entries(SIDE_ITEM_TYPE_MATERIAL_MAP)
    .filter(
      ([_, itemMaterialFilter]) =>
        !!newMaterial &&
        itemMaterialFilter &&
        !newMaterial?.match(itemMaterialFilter)
    )
    .map(([sideType]) => sideType)
    .filter((sideType): sideType is SideItemType => sideType !== undefined);

/**
 * Filters side items that match any type in the `typesToRemove`.
 *
 * @param sideItems - List of side items from a shape.
 * @param typesToRemove - List of types to match against.
 * @returns Filtered side items.
 */
const filterSideItemsByType = (
  sideItems: Partial<SideItemInfoModel>[],
  typesToRemove: SideItemType[]
) =>
  sideItems.filter((item) =>
    typesToRemove.some((type) => type === item.sideType)
  );

/**
 * Maps side items to the desired format (ResetEdge).
 *
 * @param {Object} shape - The shape from which the side item comes.
 * @param {Array} sideItems - The side items to map.
 * @returns {Array} Mapped side items in ResetEdge format.
 */
const mapSideItemsToResetEdge = (
  shape: DrawingShape,
  sideItems: Partial<SideItemInfoModel>[]
): EdgeBySideItem[] =>
  sideItems.map((sideItem) => ({
    shapeName: shape.canvasObjectName,
    edgeName: sideItem.arrowInfo?.label,
    sideItem,
  }));

/**
 * Combines all shapes and returns edges to reset based on specific conditions.
 *
 * @param {Array} shapes - The list of drawing shapes.
 * @param {Array} typesToRemove - List of side types to be removed.
 * @returns {Array} List of ResetEdge objects for edges to reset.
 */
export const getEdgesToReset = (
  shapes: DrawingShape[],
  typesToRemove: SideItemType[]
): EdgeBySideItem[] =>
  shapes
    .filter((shape) => shape.type !== ShapeType.FAUX_SHAPE)
    .flatMap((shape) => {
      const sideItems = getSideItemsFromShape(shape);
      const filteredSideItems = filterSideItemsByType(sideItems, typesToRemove);

      return mapSideItemsToResetEdge(shape, filteredSideItems);
    });

/*
 * Higher order function that returns a reducer for tear-outs.
 * @returns a reducer that processes SelectedItemWithFullRecord to ProductMatchingSummary
 */
export const createTearOutsToSummaryReducer =
  (tearOutsNewCatalog: TearOutModel[]) =>
  (
    acc: SelectedItemProductMatchingSummary,
    next: SelectedItemWithFullRecord,
    index: number
  ): SelectedItemProductMatchingSummary => {
    const ctx: CheckerContext<TearOutModel> = {
      newCatalog: tearOutsNewCatalog,
    };

    return addToSummary({
      summary: acc,
      item: next,
      index,
      checker: TearOutChecker,
      ctx,
    });
  };

/**
 * Higher order function that returns a reducer for cutouts.
 * @returns a reducer that processes SelectedItemWithFullRecord to ProductMatchingSummary
 */
export const createCutoutsToSummaryReducer =
  (cutoutsNewCatalog: CutoutModel[]) =>
  (
    acc: SelectedItemProductMatchingSummary,
    next: SelectedItemWithFullRecord,
    index: number
  ): SelectedItemProductMatchingSummary => {
    const ctx: CheckerContext<CutoutModel> = {
      newCatalog: cutoutsNewCatalog,
    };

    return addToSummary({
      summary: acc,
      item: next,
      index,
      checker: CutoutChecker,
      ctx,
    });
  };

/**
 * Higher order function that returns a reducer for plumbing.
 * @returns a reducer that processes SelectedItemWithFullRecord to ProductMatchingSummary
 */
export const createPlumbingToSummaryReducer =
  (plumbingNewCatalog: PlumbingProduct[]) =>
  (
    acc: SelectedItemProductMatchingSummary,
    next: SelectedItemWithFullRecord,
    index: number
  ): SelectedItemProductMatchingSummary => {
    const ctx: CheckerContext<PlumbingProduct> = {
      newCatalog: plumbingNewCatalog,
    };

    return addToSummary({
      summary: acc,
      item: next,
      index,
      checker: PlumbingChecker,
      ctx,
    });
  };

export const createSelectedItemToSummaryReducer =
  (newCatalog: OptionalItem[]) =>
  (
    acc: ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    >,
    next: SelectedItemWithFullRecord,
    index: number
  ): ProductMatchingSummary<
    SelectedItemWithFullRecord,
    SelectedItemWithFullRecord
  > => {
    const ctx: CheckerContext<OptionalItem> = {
      newCatalog,
    };

    return addToSummary({
      summary: acc,
      item: next,
      index,
      checker: SelectedItemChecker,
      ctx,
    });
  };
